import React, { useState, useEffect } from 'react';
import { FileText, TrendingUp, TrendingDown, AlertCircle, CheckCircle, Play, Pause } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { getGoogleAdsClient, getDateRange } from '../lib/google-ads-client';
import toast from 'react-hot-toast';
import { useGoogleAdsCompany, GOOGLE_ADS_COMPANY_OPTIONS } from '@/hooks/useGoogleAdsCompany';

interface AdVariant {
  id: string;
  headline1: string;
  headline2: string;
  headline3?: string;
  description1: string;
  description2?: string;
  finalUrl: string;
  status: string;
  impressions: number;
  clicks: number;
  ctr: number;
  conversions: number;
  conversionRate: number;
  costMicros: number;
  avgCpcMicros: number;
}

interface AdTest {
  id: string;
  name: string;
  adGroupId: string;
  adGroupName: string;
  campaignId: string;
  campaignName: string;
  startDate: string;
  status: 'active' | 'paused' | 'completed';
  variants: AdVariant[];
  winner?: string;
  confidenceLevel?: number;
}

export default function AdCopyPerformancePage() {
  const [loading, setLoading] = useState(true);
  const [adTests, setAdTests] = useState<AdTest[]>([]);
  const [selectedTest, setSelectedTest] = useState<AdTest | null>(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const { company, setCompany } = useGoogleAdsCompany();
  const [dateRange] = useState('last_30_days');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load customer
      const { data: customerData } = await supabase
        .from('google_ads_customer_mapping')
        .select('*')
        .eq('is_active', true)
        .limit(1)
        .maybeSingle();

      if (customerData) {
        setSelectedCustomerId(customerData.customer_id);
        await loadAdTests(customerData.customer_id);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdTests = async (customerId: string) => {
    try {
      const client = getGoogleAdsClient(customerId);
      const range = getDateRange(30);

      // Get ad performance from database
      const { data: adData } = await supabase
        .from('google_ads_ad_performance')
        .select('*')
        .gte('date', range.startDate)
        .lte('date', range.endDate)
        .order('impressions', { ascending: false });

      if (adData) {
        // Group ads by ad group for A/B testing analysis
        const groupedTests = groupAdsByAdGroup(adData);
        setAdTests(groupedTests);
        if (groupedTests.length > 0) {
          setSelectedTest(groupedTests[0]);
        }
      }
    } catch (error) {
      console.error('Error loading ad tests:', error);
    }
  };

  const groupAdsByAdGroup = (adData: any[]): AdTest[] => {
    const grouped = new Map<string, any[]>();

    adData.forEach(ad => {
      const key = `${ad.campaign_id}_${ad.ad_group_id}`;
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)?.push(ad);
    });

    const tests: AdTest[] = [];
    let testId = 1;

    grouped.forEach((ads, key) => {
      if (ads.length >= 2) { // Only include ad groups with multiple ads
        const firstAd = ads[0];
        
        // Calculate winner and confidence
        const sortedByConversions = [...ads].sort((a, b) => b.conversions - a.conversions);
        const winner = sortedByConversions[0];
        const confidence = calculateConfidence(sortedByConversions[0], sortedByConversions[1]);

        tests.push({
          id: `test_${testId++}`,
          name: `${firstAd.campaign_name} - ${firstAd.ad_group_name}`,
          adGroupId: firstAd.ad_group_id,
          adGroupName: firstAd.ad_group_name,
          campaignId: firstAd.campaign_id,
          campaignName: firstAd.campaign_name,
          startDate: firstAd.date,
          status: 'active',
          variants: ads.map(ad => ({
            id: ad.ad_id,
            headline1: ad.headline_1 || 'Headline 1',
            headline2: ad.headline_2 || 'Headline 2',
            headline3: ad.headline_3,
            description1: ad.description_1 || 'Description 1',
            description2: ad.description_2,
            finalUrl: ad.final_url || '',
            status: ad.ad_status,
            impressions: ad.impressions || 0,
            clicks: ad.clicks || 0,
            ctr: ad.ctr || 0,
            conversions: ad.conversions || 0,
            conversionRate: ad.clicks > 0 ? (ad.conversions / ad.clicks) * 100 : 0,
            costMicros: ad.cost_micros || 0,
            avgCpcMicros: ad.avg_cpc_micros || 0
          })),
          winner: winner.ad_id,
          confidenceLevel: confidence
        });
      }
    });

    return tests;
  };

  const calculateConfidence = (ad1: any, ad2: any): number => {
    // Simplified confidence calculation based on conversion difference
    if (!ad2 || ad1.conversions === 0) return 0;
    
    const conversionDiff = Math.abs(ad1.conversions - ad2.conversions);
    const totalConversions = ad1.conversions + ad2.conversions;
    
    if (totalConversions === 0) return 0;
    
    const confidencePercent = (conversionDiff / totalConversions) * 100;
    return Math.min(confidencePercent, 95); // Cap at 95%
  };

  const pauseLosingVariant = async (test: AdTest, variantId: string) => {
    if (!confirm('Are you sure you want to pause this ad variant?')) {
      return;
    }

    try {
      // Update in database
      await supabase
        .from('google_ads_ad_performance')
        .update({ ad_status: 'PAUSED' })
        .eq('ad_id', variantId);

      alert('Ad variant paused successfully');
      loadData();
    } catch (error) {
      console.error('Error pausing variant:', error);
      alert('Failed to pause variant');
    }
  };

  const getVariantComparison = (variant: AdVariant, test: AdTest) => {
    const avgCtr = test.variants.reduce((sum, v) => sum + v.ctr, 0) / test.variants.length;
    const avgConvRate = test.variants.reduce((sum, v) => sum + v.conversionRate, 0) / test.variants.length;

    return {
      ctrDiff: variant.ctr - avgCtr,
      convRateDiff: variant.conversionRate - avgConvRate,
      isWinner: variant.id === test.winner
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading ad performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Ad Copy Performance Tracker</h1>
          <p className="text-gray-600 mt-1">
            A/B test analysis and performance comparison for ad variants
          </p>
        </div>
      </div>
      <div className="flex justify-end mb-4">
        <select
          value={company}
          onChange={(e) => setCompany(e.target.value as 'hales' | 'myccs' | 'abraham')}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
        >
          {GOOGLE_ADS_COMPANY_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active Tests</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {adTests.filter(t => t.status === 'active').length}
              </p>
            </div>
            <Play className="w-8 h-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Variants</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {adTests.reduce((sum, t) => sum + t.variants.length, 0)}
              </p>
            </div>
            <FileText className="w-8 h-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Avg Confidence</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {(adTests.reduce((sum, t) => sum + (t.confidenceLevel || 0), 0) / adTests.length).toFixed(0)}%
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-purple-600" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Clear Winners</p>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {adTests.filter(t => (t.confidenceLevel || 0) > 80).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
      </div>

      {/* Tests List and Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tests List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-4 border-b border-gray-200">
              <h3 className="font-semibold">Active A/B Tests</h3>
            </div>
            <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
              {adTests.map(test => (
                <button
                  key={test.id}
                  onClick={() => setSelectedTest(test)}
                  className={`w-full text-left p-4 hover:bg-gray-50 transition-colors ${
                    selectedTest?.id === test.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm">{test.campaignName}</h4>
                      <p className="text-xs text-gray-600 mt-1">{test.adGroupName}</p>
                      <div className="flex items-center space-x-3 mt-2">
                        <span className="text-xs text-gray-500">{test.variants.length} variants</span>
                        {test.confidenceLevel && test.confidenceLevel > 80 && (
                          <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded">
                            {test.confidenceLevel.toFixed(0)}% confident
                          </span>
                        )}
                      </div>
                    </div>
                    {test.status === 'active' && (
                      <span className="flex h-3 w-3 relative mt-1">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Test Details */}
        <div className="lg:col-span-2">
          {selectedTest ? (
            <div className="space-y-4">
              {/* Test Info */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedTest.name}</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Campaign:</span>
                    <span className="ml-2 font-medium">{selectedTest.campaignName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Ad Group:</span>
                    <span className="ml-2 font-medium">{selectedTest.adGroupName}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Variants:</span>
                    <span className="ml-2 font-medium">{selectedTest.variants.length}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Confidence:</span>
                    <span className="ml-2 font-medium">{selectedTest.confidenceLevel?.toFixed(0)}%</span>
                  </div>
                </div>
              </div>

              {/* Variants Comparison */}
              <div className="space-y-4">
                {selectedTest.variants.map((variant, index) => {
                  const comparison = getVariantComparison(variant, selectedTest);
                  
                  return (
                    <div
                      key={variant.id}
                      className={`bg-white rounded-lg shadow-sm border-2 p-6 ${
                        comparison.isWinner ? 'border-green-500' : 'border-gray-200'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="font-semibold text-gray-900">Variant {String.fromCharCode(65 + index)}</h4>
                            {comparison.isWinner && (
                              <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded font-medium">
                                Winner
                              </span>
                            )}
                            <span className={`px-2 py-1 text-xs rounded ${
                              variant.status === 'ENABLED'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                            }`}>
                              {variant.status}
                            </span>
                          </div>
                        </div>
                        {!comparison.isWinner && variant.status === 'ENABLED' && (
                          <button
                            onClick={() => pauseLosingVariant(selectedTest, variant.id)}
                            className="flex items-center space-x-1 px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            <Pause className="w-3 h-3" />
                            <span>Pause</span>
                          </button>
                        )}
                      </div>

                      {/* Ad Copy */}
                      <div className="bg-gray-50 rounded-lg p-4 mb-4">
                        <div className="space-y-2">
                          <div>
                            <span className="text-xs text-gray-600">Headlines:</span>
                            <p className="font-medium text-sm">{variant.headline1}</p>
                            <p className="font-medium text-sm">{variant.headline2}</p>
                            {variant.headline3 && <p className="font-medium text-sm">{variant.headline3}</p>}
                          </div>
                          <div>
                            <span className="text-xs text-gray-600">Descriptions:</span>
                            <p className="text-sm">{variant.description1}</p>
                            {variant.description2 && <p className="text-sm">{variant.description2}</p>}
                          </div>
                        </div>
                      </div>

                      {/* Metrics */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-600">Impressions</p>
                          <p className="text-lg font-bold text-gray-900">{variant.impressions.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Clicks</p>
                          <p className="text-lg font-bold text-gray-900">{variant.clicks.toLocaleString()}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">CTR</p>
                          <div className="flex items-center space-x-1">
                            <p className="text-lg font-bold text-gray-900">{variant.ctr.toFixed(2)}%</p>
                            {comparison.ctrDiff > 0 ? (
                              <TrendingUp className="w-4 h-4 text-green-600" />
                            ) : comparison.ctrDiff < 0 ? (
                              <TrendingDown className="w-4 h-4 text-red-600" />
                            ) : null}
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600">Conv. Rate</p>
                          <div className="flex items-center space-x-1">
                            <p className="text-lg font-bold text-gray-900">{variant.conversionRate.toFixed(2)}%</p>
                            {comparison.convRateDiff > 0 ? (
                              <TrendingUp className="w-4 h-4 text-green-600" />
                            ) : comparison.convRateDiff < 0 ? (
                              <TrendingDown className="w-4 h-4 text-red-600" />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 mb-2">No A/B test selected</p>
              <p className="text-sm text-gray-500">
                Select a test from the list to view detailed performance comparison
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Empty State */}
      {adTests.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <AlertCircle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600 mb-2">No ad tests found</p>
          <p className="text-sm text-gray-500">
            Ad groups with multiple active ads will appear here for A/B testing analysis
          </p>
        </div>
      )}
    </div>
  );
}
